// src/pages/Checkout.js
import React from "react";
import { useLocation } from "react-router-dom";
import { Elements, PaymentElement, useStripe, useElements } from "@stripe/react-stripe-js";
import { loadStripe } from "@stripe/stripe-js";
import Layout from "../components/Layout";

// Load Stripe with your publishable key.
const stripePromise = loadStripe(process.env.REACT_APP_STRIPE_PK);

const CheckoutForm = ({ orderNumber }) => {
  const stripe = useStripe();
  const elements = useElements();

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (!stripe || !elements) {
      console.log("[CheckoutForm] Stripe or elements not ready.");
      return;
    }
    // Append the orderNumber as a query parameter in the return URL.
    const return_url = `${window.location.origin}/orderSummary?orderId=${orderNumber}`;
    const result = await stripe.confirmPayment({
      elements,
      confirmParams: { return_url },
    });

    if (result.error) {
      console.error("[CheckoutForm] Payment error:", result.error.message);
    } else {
      console.log("[CheckoutForm] Payment confirmed, redirecting...");
    }
  };

  return (
    <form onSubmit={handleSubmit} className="w-full">
      <div id="payment-element" style={{ marginBottom: "24px", minHeight: "200px" }}>
        <PaymentElement />
      </div>
      <button
        disabled={!stripe}
        className="mt-4 inline-flex items-center justify-center px-6 py-3 text-base font-bold text-white bg-blue-600 rounded-md"
      >
        Submit Payment
      </button>
    </form>
  );
};

const Checkout = () => {
  const location = useLocation();
  // Retrieve values passed via navigation state.
  const clientSecret = location.state?.clientSecret;
  const orderNumber = location.state?.orderNumber; // order number passed from CreditPlan.js
  const purchaseDetails = location.state?.purchaseDetails;
  const userId =
    location.state?.userId ||
    (localStorage.getItem("authData") ? JSON.parse(localStorage.getItem("authData")).user.sub : null);

  console.log("[Checkout] clientSecret:", clientSecret);
  console.log("[Checkout] orderNumber:", orderNumber);
  console.log("[Checkout] purchaseDetails:", purchaseDetails);
  console.log("[Checkout] userId:", userId);

  // If any required data is missing, display an error.
  if (!clientSecret || !orderNumber || !userId) {
    return (
      <Layout>
        <div className="container mx-auto mt-10 max-w-7xl bg-white p-10 rounded-md">
          <h2 className="text-xl font-bold">Payment or Order Information Missing</h2>
          <p className="mt-2 text-gray-600">
            Please try again or contact support if the problem persists.
          </p>
        </div>
      </Layout>
    );
  }

  const options = { clientSecret };

  return (
    <Layout>
      <div className="container mx-auto mt-10 max-w-7xl bg-white shadow-md p-10 rounded-md">
        <h1 className="text-xl font-semibold mb-6">Complete Your Purchase</h1>
        <div className="flex flex-col lg:flex-row gap-8">
          {/* Order Summary */}
          <div className="w-full lg:w-1/2 p-4 border border-gray-200 rounded-md bg-gray-50">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">Order Summary</h2>
            {purchaseDetails ? (
              <div className="space-y-4">
                <div className="grid grid-cols-2">
                  <span className="text-sm font-medium text-gray-500">Plan</span>
                  <span className="text-sm font-bold text-gray-900">{purchaseDetails.description}</span>
                </div>
                <div className="grid grid-cols-2">
                  <span className="text-sm font-medium text-gray-500">Price</span>
                  <span className="text-sm font-bold text-gray-900">${purchaseDetails.amount}</span>
                </div>
                <div className="grid grid-cols-2">
                  <span className="text-sm font-medium text-gray-500">Auto‑renew</span>
                  <span className="text-sm font-bold text-gray-900">
                    {purchaseDetails.autoRenew ? "Enabled" : "Disabled"}
                  </span>
                </div>
                <div className="grid grid-cols-2">
                  <span className="text-sm font-medium text-gray-500">Option</span>
                  <span className="text-sm font-bold text-gray-900">{purchaseDetails.option}</span>
                </div>
                <div className="grid grid-cols-2">
                  <span className="text-sm font-medium text-gray-500">Order Number</span>
                  <span className="text-sm font-bold text-gray-900">#{orderNumber}</span>
                </div>
              </div>
            ) : (
              <p className="text-sm text-gray-500">No order details available.</p>
            )}
          </div>

          {/* Stripe Payment Form */}
          <div className="w-full lg:w-1/2">
            <Elements stripe={stripePromise} options={options}>
              <CheckoutForm orderNumber={orderNumber} />
            </Elements>
          </div>
        </div>
      </div>
    </Layout>
  );
};

export default Checkout;
